import styled from 'styled-components'
import { useCallback, useEffect, useState } from 'react'
import { useClient } from '../../utils/useClient'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { getMarketLanguageState } from '../../store/appSlice'
import { getSessionInitDataState } from '../../store/sessionDataSlice'
import { getSidePanelState, setReadMoreData } from '../../store/sidePanelSlice'
import { ReadMoreData } from '../../store/types'
import { TestElementTypeId } from '../../types/TestAttributeId'
import convertNewlineToJSX from '../../utils/convertNewlineToJSX'
import { ScaniaReadMoreTextNode } from '../../api/generated'
import { TdsSpinner } from '@scania/tegel-react'

const ReadMoreRoot = styled.div``

const TextContainer = styled.div<SpinnerProps>`
  margin: 16px;
  display: ${(props) => (props.$showSpinner ? 'none' : '')};
`

const Header = styled.h4`
  margin-bottom: 16px;
`

const ReadmoreImage = styled.img`
  max-width: 100%;
`

const ReadmoreImageSmallContainer = styled.div``

interface ReadMorImageSmallProps {
  $isSelectedImage: boolean
}

const ReadmoreImageSmall = styled.img<ReadMorImageSmallProps>`
  width: 20%;
  padding: 4px;
  overflow: hidden;
  cursor: pointer;
  border: ${(props) =>
    props.$isSelectedImage ? '1px solid var(--tds-blue-300)' : ''};

  img {
    position: relative;
    display: inline-block;
    height: auto;
    width: 100%;
  }
`

const Text = styled.div`
  font-size: 14px;
`

interface SpinnerProps {
  $showSpinner: boolean
}

const SpinnerWrapper = styled.div<SpinnerProps>`
  display: ${(props) => (props.$showSpinner ? '' : 'none')};
  margin: 16px;
  height: 40px;

  svg {
    color: var(--tds-blue-600);
  }
`

// Id and short text, the readmore text and images will be loaded by this
// component, except for some items from the Factory Models page that is
// currently handled differently. TODO: Consider unifying the readmore behavior.
export interface ReadMoreEntry {
  id: string

  /**
   * Usually "short text".
   *
   * TODO: Investigate if this can be removed with the latest version of
   * get_readmore.
   */
  title: string | null
}

export function ContentReadMore(): JSX.Element {
  const dispatch = useAppDispatch()
  const client = useClient()
  const marketLanguage = useAppSelector(getMarketLanguageState)
  const sessionInitData = useAppSelector(getSessionInitDataState)
  const sessionId = sessionInitData?.sessionId
  const { readMoreData, readMoreNode } = useAppSelector(getSidePanelState)
  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null)
  const [showSpinner, setShowSpinner] = useState<boolean>(false)

  const handleReadMoreGuidedView = useCallback(async () => {
    const id = readMoreNode?.id
    if (!marketLanguage || !client || !id || !sessionId) {
      return
    }
    dispatch(setReadMoreData(null))
    setCurrentImageUrl(null)
    setShowSpinner(true)
    const readMoreResult = await client.getReadmore({
      sessionId,
      itemId: id,
    })
    setShowSpinner(false)
    const images = readMoreResult.resources.filter(
      (resource) => resource.resourceType === 'Image',
    )
    let imageUrls: string[] = []
    if (images && images?.length > 0) {
      images.forEach((image) => {
        if (image.path) {
          imageUrls.push(encodeURI(image.path))
        }
      })
      setCurrentImageUrl(imageUrls[0])
    }

    // TODO: Provide the submenu name as well since most options are called
    // "with" or "without".
    const title = readMoreNode?.title || readMoreResult.shortText || ''

    const newData: ReadMoreData = {
      label: title,
      images: imageUrls,
      text: readMoreResult.readmoreText,
    }
    dispatch(setReadMoreData(newData))
  }, [client, marketLanguage, sessionId, dispatch, readMoreNode])

  // Figure out which readmore data source to display.
  //
  // TODO: Consider unifying this dataflow in the Redux Actions or Reducer
  // layers instead?
  //
  // TODO: Every use case should be covered by getReadmoreV2 now, use a single
  // item id in Redux or as a component property as input for this component.
  useEffect(() => {
    if (readMoreNode) {
      handleReadMoreGuidedView()
    }
  }, [handleReadMoreGuidedView, readMoreNode])

  // set current image url
  useEffect(() => {
    if (readMoreData) {
      setCurrentImageUrl(readMoreData.images[0])
    }
  }, [handleReadMoreGuidedView, readMoreData])

  const handleImageClick = (e: any) => {
    setCurrentImageUrl(e.target.src)
  }

  const capitalizeFirstLetter = (text: string | null | undefined) => {
    if (!text) {
      return null
    }
    const capitalizedText = text.charAt(0).toUpperCase() + text.slice(1)
    return capitalizedText
  }

  interface ReadMoreContentProps {
    readMore: ScaniaReadMoreTextNode[]
  }

  function ReadMoreContent(props: ReadMoreContentProps): JSX.Element {
    const renderReadMoreContent = (data: ScaniaReadMoreTextNode[]) => {
      let result: JSX.Element[] = []
      let listItems: JSX.Element[] | null = null
      let keycounter = 0

      data.forEach((item) => {
        if (item.textParagraph) {
          if (listItems) {
            result.push(<ul key={'ul' + keycounter}>{listItems}</ul>)
            listItems = null
            keycounter++
          }
          let paragraph = convertNewlineToJSX(item.textParagraph)
          if (paragraph) {
            result.push(paragraph)
            keycounter++
          }
        } else if (item.textList) {
          if (!listItems) {
            listItems = []
          }
          listItems.push(<li key={'li' + keycounter}>{item.textList}</li>)
          keycounter++
        }
      })

      if (listItems) {
        result.push(<ul key={'ul' + keycounter}>{listItems}</ul>)
      }

      return result
    }

    return <>{renderReadMoreContent(props.readMore)}</>
  }

  return (
    <ReadMoreRoot>
      <SpinnerWrapper $showSpinner={showSpinner}>
        <TdsSpinner size="sm" />
      </SpinnerWrapper>
      <TextContainer $showSpinner={showSpinner}>
        <Header>{capitalizeFirstLetter(readMoreData?.label) || ''}</Header>
        {currentImageUrl ? (
          <ReadmoreImage src={currentImageUrl} alt={readMoreData?.label} />
        ) : (
          <></>
        )}
        <ReadmoreImageSmallContainer>
          {readMoreData?.images && readMoreData.images.length > 1 ? (
            readMoreData.images.map((url, i) => {
              return (
                <ReadmoreImageSmall
                  $isSelectedImage={url === currentImageUrl}
                  src={url}
                  key={'' + i + ', ' + url}
                  onClick={(event) => handleImageClick(event)}
                  alt={readMoreData?.label}
                />
              )
            })
          ) : (
            <></>
          )}
        </ReadmoreImageSmallContainer>

        <Text
          data-test-element-type={
            TestElementTypeId.GuidedOfferingInfoReadMoreText
          }
        >
          {readMoreData?.text && (
            <ReadMoreContent readMore={readMoreData.text}></ReadMoreContent>
          )}
        </Text>
      </TextContainer>
    </ReadMoreRoot>
  )
}
